/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {
    useEffect,
    useState
} from "react";
import {usePrimeCache} from "./ServiceHooks";
import {getFriendlyAnswersForQuestions, allSurveyQuestions, useOnlyWhen} from "../../../monitoring/src/Hooks/SharedSurveyHooks";
import {service as SurveyQuestionDataTypeService} from "../Services/SurveyQuestionDataTypeService";
import {
    service as SurveyQuestionSecondaryFieldRequiredService
} from "../Services/SurveyQuestionSecondaryFieldRequiredService";

export const getSectionElementId = (section) => {
    return "sb-survey-section-" + section.surveySectionId;
}

// SB-3022: resolve by question `code` (every answer carries its question's code), so completion
// survives a subprogram move where answers keep a stale surveyQuestionId but the same code.
const getAnswerForQuestion = (answers, question) => {
    return answers?.find?.((x) => (x.code != null && x.code !== '') && String(x.code) === String(question?.code)) ?? null;
}

/**
 * PARALLEL LOGIC — keep in sync with:
 *   server/app/lib/App/Service/SurveySubmissionService.php :: secondaryFieldIsComplete()
 */
const isSecondaryQuestionAcceptable = (answer, req, primaryLogic, secondaryLogic) => {
    if (!primaryLogic(answer)) {
        return false;
    }
    
    //check the secondary field based on how the question is configured.
    switch (req.code) {
        case "yes":
            //since the primary field is good. We just need to make sure this is too.
            return secondaryLogic(answer);
        case "no":
            //we don't care about the second field. So we're good.
            return true;
        case "when_yes":
            if (answer.valueBool != null) {
                if (Boolean(answer.valueBool) === true) {
                    return secondaryLogic(answer);
                }
                
                //if we're no then we can call this good.
                return true;
            }
            
            //if bool isn't we should fail this. In theory we shouldn't hit this code, but I want to be safe.
            return false;
        case "when_no":
            if (answer.valueBool != null) {
                if (Boolean(answer.valueBool) === false) {
                    return secondaryLogic(answer);
                }
                
                //if we're yes then we can call this good.
                return true;
            }
            
            //if bool isn't we should fail this. In theory we shouldn't hit this code, but I want to be safe.
            return false;
        default:
            console.log("Unknown question requirement type to validate: " + req.code);
            break;
    }
    
    
    //if we've made it here something went wrong. Let's just fail things.
    return false;
};

const isTextFileAcceptable = (answer) => {
    //if the file is set we're good.
    if (answer.fileUploadId != null) {
        return true;
    } else if (answer.valueText != null && answer.valueText !== "") {
        //or if the text field is set.
        return true;
    }
    //the file and text field are also acceptable. so we don't need to check for that above.
    
    return false;
};

/**
 * Returns true if the given answer satisfies the completion requirement for the question's data type.
 *
 * PARALLEL LOGIC — keep in sync with:
 *   server/app/lib/App/Service/SurveySubmissionService.php :: questionHasValue()
 * If this function changes, update the PHP counterpart too (and vice versa).
 */
export const isQuestionComplete = (question, answer, conservationAction) => {
    const req = SurveyQuestionSecondaryFieldRequiredService.CachedValueById(question.surveyQuestionSecondaryFieldRequiredId)?.result;
    const type = SurveyQuestionDataTypeService.CachedValueById(question.surveyQuestionDataTypeId)?.result;
    
    if (answer === null || type === null || req === null) {
        return false;
    }

    switch (type.code) {
        case "text":
            return answer.valueText != null && answer.valueText !== "";
        case "bool":
            return answer.valueBool != null;
        case "file":
            return answer.fileUploadId != null;
        case "number":
            return answer.valueNumeric != null && answer.valueNumeric !== "";
        case "webmap":
            return answer.valueText != null && answer.valueText !== "";
        case "date":
            return answer.valueDate != null && answer.valueDate !== "";
        case "date_bool":
            return isSecondaryQuestionAcceptable(
                answer,
                req,
                (x) => x.valueBool != null,
                (x) => x.valueDate != null && x.valueDate !== ""
            );
        case "text_bool":
            return isSecondaryQuestionAcceptable(
                answer,
                req,
                (x) => x.valueBool != null,
                (x) => x.valueText != null && x.valueText !== ""
            );
        case "file_bool":
            return isSecondaryQuestionAcceptable(
                answer,
                req,
                (x) => x.valueBool != null,
                (x) => x.fileUploadId != null
            );
        case "text_file":
            return isTextFileAcceptable(answer);
        case "permit_bool":
            if (answer.valueBool === 0 && answer.valueText) { // if no is selected
                return true;
            } else if (answer.valueBool === 1) {
                // or yes and the permit is filled out.
                return conservationAction && conservationAction?.ConservationActionPermits?.some(
                    caPermit => caPermit.Permit?.permitTypeId === question.permitTypeId
                );
            }
            break;
        case "contact_relationship":
            // Legacy free-text from before this type was introduced counts as complete.
            if (!answer?.valueNumeric && answer?.valueText) return true;
            // Otherwise require a selected contact that has all required fields on file.
            // valueBool is written by SurveyFormInputContactRelationship to reflect hasContactInfo.
            return Boolean(answer?.valueBool) === true;
        case "bool_prerequisite":
            // PARALLEL LOGIC — keep in sync with:
            //   server/app/lib/App/Service/SurveySubmissionService.php :: questionHasValue()
            // Complete only when explicitly true; null and 0 both mean incomplete.
            return answer?.valueBool === 1 || answer?.valueBool === true;
        case "label":
            break;
        case "radio":
        case "many-fields":
            return (answer.valueText ?? "") !== "";
        default:
            console.log("Unknown data type to validate: " + type.code);
            return false;
    }
}

/**
 * Returns a function that evaluates whether a section is complete given the current answers.
 * Filters out questions hidden by onlyWhen rules before checking completion.
 *
 * PARALLEL LOGIC — keep in sync with:
 *   server/app/lib/App/Service/SurveySubmissionService.php :: computePreSubmitStatusFromData()
 * If this function changes, update the PHP counterpart too (and vice versa).
 */
export const useIsSectionComplete = (survey, conservationAction, customSections, customData) => {
    
    const loaded = usePrimeCache([SurveyQuestionDataTypeService, SurveyQuestionSecondaryFieldRequiredService]);
    const {isFieldVisible, getVisibleQuestions} = useOnlyWhen();
    
    return (section, answers) => {
        //if we don't have the data on how to define completed questions we can only assume everything isn't finished.
        if (!loaded) {
            return false;
        }
        
        if (customSections?.[section.code]?.isSectionComplete) {
            return customSections[section.code].isSectionComplete(survey, section, customData);
        }
        
        const friendlyAnswers = getFriendlyAnswersForQuestions(answers, section.SurveyQuestions);
        
        const requiredVisibleQuestions = section.SurveyQuestions.filter(
            question => question.isRequired && isFieldVisible(question.options, friendlyAnswers)
        );

        return requiredVisibleQuestions.every( (question) =>
            isQuestionComplete(question, getAnswerForQuestion(answers, question), conservationAction)
        )
    }
}

export function useElementIsAbove(elementId, threshold = 0) {
    const [isAbove, setIsAbove] = useState(false);
    
    useEffect(() => {
        const handleScroll = () => {
            const el = document.getElementById(elementId);
            
            if (el) {
                const top = el.getBoundingClientRect().top;
                setIsAbove(top < threshold);
            } else {
                setIsAbove(false);
            }
        };
        
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // initial check
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, [elementId, threshold]);
    
    return isAbove;
}

export const useSectionsWithCompletion = (survey, conservationAction, customSections, customData, answers) => {
    
    const isSectionComplete = useIsSectionComplete(survey, conservationAction, customSections, customData);
    const {SurveySections:sections = []} = survey;

    return sections
        .map((section) => {
            return {
                ...section,
                isSectionComplete: isSectionComplete(section, answers),
            };
        })
        .sort((a, b) => Number(a.order) - Number(b.order));
}
